import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, type To } from 'react-router-dom'

import classes from './DashboardMetricCard.module.scss'
import { DashboardCard } from '@percent/workplace-giving/app/Home/DashboardCard/DashboardCard'
import { Box, Flex, Icon, IconProps, Skeleton, Tooltip, Typography } from '@percent/lemonade'

type DashboardMetricCardProps = {
  title: string
  titleTooltip?: string
  value: string | number
  compactValue?: string
  isLoading: boolean
  icon?: IconProps['name']
  iconElement?: ReactNode
  testId: string
  description?: string
  href?: To
}

export function DashboardMetricCard({
  title,
  titleTooltip,
  value,
  compactValue,
  isLoading,
  icon,
  iconElement,
  testId,
  description,
  href
}: DashboardMetricCardProps): JSX.Element {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const hiddenRef = useRef<HTMLSpanElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)

  useEffect(() => {
    if (!compactValue || !wrapperRef.current || !hiddenRef.current) return undefined

    const check = () => {
      if (wrapperRef.current && hiddenRef.current) {
        setIsOverflowing(hiddenRef.current.offsetWidth > wrapperRef.current.offsetWidth)
      }
    }

    check()
    const observer = new ResizeObserver(check)
    observer.observe(wrapperRef.current)

    return () => observer.disconnect()
  }, [value, compactValue])

  const isLong = compactValue && typeof value === 'string' && value.length > 10
  const displayValue = (isOverflowing || isLong) && compactValue ? compactValue : value

  const titleTypography = (
    <Typography variant="default" $color="black" data-testid={`${testId}-title`}>
      {title}
    </Typography>
  )

  const card = (
    <DashboardCard icon={icon} iconElement={iconElement} testId={testId} gradient compactOnMobile>
      <Box className={classes.Spacer} />
      <Box className={classes.ContentWrapper}>
        {/* Only wrap when there is an icon to sit beside the title — cards without a tooltip keep
            their original markup, so text-based selectors elsewhere are unaffected. */}
        {titleTooltip ? (
          <Flex align="center" gap={4}>
            {titleTypography}
            <Tooltip content={titleTooltip} placement="top">
              <div className={classes.TitleTooltipIcon} data-testid={`${testId}-title-tooltip`}>
                <Icon name="info" size={5} color="neutral300" />
              </div>
            </Tooltip>
          </Flex>
        ) : (
          titleTypography
        )}
        {isLoading ? (
          <Skeleton data-testid={`${testId}-skeleton`} height="2rem" radius="xl" width="120px" />
        ) : (
          <div ref={wrapperRef} className={classes.ValueWrapper}>
            <span ref={hiddenRef} className={classes.HiddenValue}>
              {value}
            </span>
            {displayValue === value ? (
              <Typography variant="h2" $color="black" data-testid={`${testId}-value`}>
                {displayValue}
              </Typography>
            ) : (
              <Tooltip content={value} placement="top">
                <Typography variant="h2" $color="black" data-testid={`${testId}-value`}>
                  {displayValue}
                </Typography>
              </Tooltip>
            )}
          </div>
        )}
        {description && (
          <Typography variant="small" $color="gray500" data-testid={`${testId}-description`}>
            {description}
          </Typography>
        )}
      </Box>
    </DashboardCard>
  )

  if (href) {
    return (
      <Link to={href} className={classes.LinkWrapper} data-testid={`${testId}-link`}>
        {card}
      </Link>
    )
  }

  return card
}
